import React, { useState } from "react";
import { X, Download, ExternalLink, Printer } from "lucide-react";
import { toast } from "sonner";
import { downloadFile } from "@/lib/downloadFile";
import PdfJsViewer from "@/components/wizard/PdfJsViewer";

/**
 * Inline PDF preview modal. Renders the PDF via PDF.js canvas so it works
 * inside sandboxed preview iframes where native browser PDF viewers are blocked.
 *
 * props:
 *   url: absolute PDF URL
 *   filename: suggested name for save
 *   title: human-readable doc title
 *   onClose()
 */
export default function PdfViewerModal({ url, filename, title, onClose }) {
  const [savingToast, setSavingToast] = useState(false);

  const save = async () => {
    if (savingToast) return;
    setSavingToast(true);
    try {
      await downloadFile(url, filename);
      toast.success(`Saved ${filename}`);
    } catch { /* helper toasts */ }
    setTimeout(() => setSavingToast(false), 500);
  };
  const print = () => {
    // Ask the browser to print the PDF — opens in a new tab with print dialog.
    // Works for most sandbox configs because print dialogs are user-gesture.
    try {
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.info("Use the Save button, then print the saved PDF.");
    } catch {
      toast.info("Use the Save button, then print the saved PDF.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[95] bg-[#0B1E3A]/70 backdrop-blur-sm flex flex-col"
      data-testid="pdf-viewer-modal"
      onClick={onClose}
    >
      <div
        className="m-3 md:m-6 rounded-2xl bg-white shadow-[0_24px_64px_rgba(26,33,28,0.3)] border border-[#D6E3EF] overflow-hidden flex flex-col flex-1 min-h-0"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#D6E3EF] bg-[#F0F6FC]">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#3A5275] font-semibold">Preview</div>
            <div className="font-display text-[15px] font-semibold text-[#0B1E3A] truncate">{title}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={print} className="btn-ghost !h-9" data-testid="pdf-viewer-print">
              <Printer className="w-4 h-4" /> Print
            </button>
            <button onClick={save} className="btn-primary !h-9" data-testid="pdf-viewer-save">
              <Download className="w-4 h-4" /> Save
            </button>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost !h-9"
              data-testid="pdf-viewer-newtab"
              title="Open in a standalone tab"
            >
              <ExternalLink className="w-4 h-4" /> New tab
            </a>
            <button
              onClick={onClose}
              className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-[#3A5275] hover:bg-[#E7F1FB]"
              aria-label="Close"
              data-testid="pdf-viewer-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>
        <div className="flex-1 min-h-0">
          <PdfJsViewer url={url} />
        </div>
      </div>
    </div>
  );
}
